import React, { useState } from 'react';

import moment from 'moment';
import { useQuery, keepPreviousData } from '@tanstack/react-query';

import Layout from '../../containers/Layout';
import Bill from '../../components/Bill';
import List from '../../components/List';
import Pagination from '../../components/Pagination';

import PaymentService from '../../services/PaymentService';
import sumDollarAmounts from '../../helpers/sumDollarAmounts';
import formatCurrency from '../../helpers/formatCurrency';
import { createAchCheckPaymentStatus } from '../../helpers/createAchCheckStatus';

const getStatusColumnTitle = (source) => {
  if (source === 'ACH') {
    return 'ACH Status';
  }
  if (source === 'Outside' || source === 'Import') {
    return '';
  }
  return 'Check Status';
};

const createPaymentParts = (payment) => {
  const parts = [
    {
      title: 'Date',
      field: 'date',
      link: `/loans/${payment.loan_id}/payments/${payment.id}?tab=Payment`,
    },
    {
      title: 'Amount',
      field: 'amount',
      format: (value) => `$${formatCurrency(value)}`,
    },
    {
      title: 'Borrower',

      link: `/loans/${payment.loan_id}?tab=Borrowers`,
      format: (value, row) => {
        return row?._loan?._borrower_primary?.TIN_type === 'EIN'
          ? row?._loan._borrower_primary.entity_name
          : `${row?._loan._borrower_primary.first_name || ''} ${
              row?._loan._borrower_primary.last_name || ''
            }`;
      },
    },
    {
      title: 'Loan',
      field: '_loan._property.address_line_1',
      link: `/loans/${payment.loan_id}?tab=Property`,
    },
    {
      title: 'Source',
      field: 'source',
    },
    {
      title: getStatusColumnTitle(payment.source),
      field: 'source',
      format: (value, row) => {
        return createAchCheckPaymentStatus(row);
      },
    },
    {
      title: 'Reference',
      field: 'reference',
    },
    {
      title: 'Status',
      field: 'status',
    },
    {
      title: 'Distributed',
      field: 'distributed',
    },
    {
      title: 'Lender',
      field: '_loan._lender.name',
    },
  ];

  return parts;
};

const createCols = (payment) => [
  {
    title: 'Invoice Number',
    keys: ['_invoice.pixel_invoice_number'],
    link: (row) => {
      return `/loans/${row._invoice?.loan_id}/invoices/${row._invoice?.id}?tab=Invoice`;
    },
  },
  {
    title: 'Date',
    keys: ['_invoice.date'],
    format: (value) => moment(value).format('M/D/YY'),
  },
  {
    title: 'Line Item Account',
    keys: ['_account.name'],
  },
  {
    title: 'Line Item Description',
    keys: ['description'],
  },
  {
    title: 'Line Item Status',
    keys: ['status'],
  },
  {
    title: 'Amount Applied',
    keys: ['_amount_paid.total'],
    type: 'currency',
    format: (_, row) => {
      const paymentLineItems = row._payment_line_items?.filter(
        (item) => item.payment_id === payment.id
      );
      return sumDollarAmounts(paymentLineItems, 'amount');
    },
  },
];

export default function PaymentsPage() {
  const [currentPage, setCurrentPage] = useState(1);

  const { data: payments } = useQuery({
    queryKey: ['payments', currentPage],
    queryFn: () => PaymentService.getPayments({ page: currentPage, limit: 5 }),
    placeholderData: keepPreviousData,
  });

  const switchToPage = (selected) => {
    const page = selected.selected + 1;
    if (page < 1 || page > payments?.pageTotal) {
      return;
    }

    setCurrentPage(page);
  };

  return (
    <Layout>
      <div className="flex">
        {/* Content area */}
        <div className="relative flex flex-col flex-1 overflow-y-auto overflow-x-hidden">
          <main className="grow">
            <div className="px-4 sm:px-6 lg:px-8 py-8 pb-10 w-full max-w-9xl mx-auto">
              {/* Page header */}
              <div className="sm:flex sm:justify-between sm:items-center mb-8">
                {/* Left: Title */}
                <div className="mb-4 sm:mb-0">
                  <h1 className="text-2xl md:text-3xl text-slate-800 dark:text-slate-100 font-bold">
                    Payments
                  </h1>
                </div>
              </div>

              <div className="p-7 col-span-full sm:col-span-6 xl:col-span-4 bg-white dark:bg-slate-800 shadow-lg rounded-sm border border-slate-200 dark:border-slate-700 mb-10">
                <List
                  title=""
                  items={payments?.items?.map((payment) => ({
                    key: payment.id,
                    content: (
                      <Bill
                        bill={payment}
                        createPaymentParts={createPaymentParts}
                        lineItems={payment._line_items}
                        lineItemsCols={createCols(payment)}
                      />
                    ),
                  }))}
                />
              </div>
              <Pagination
                pageTotal={payments?.pageTotal}
                switchToPage={switchToPage}
                currentPage={currentPage}
              />
            </div>
          </main>
        </div>
      </div>
    </Layout>
  );
}
